// State-driven telegraphs (§11, §14.5 "every telegraph also has a sound"): ground decals rebuilt
// every frame from the presented FrameView. Bean telegraphs: the red pulsing teeter edge glow, the
// ledge stomp-target ring, the glove's red punch arc, the "sweaty hands" puddle while a bean's ledge
// cooldown runs (its next edge is a KO). Item and pebble telegraphs: the crate drop
// shadow that grows as it falls, the bomb's landing reticle and pulsing blast radius (beeping
// faster as the fuse runs out), the ghost pebble's closing target, and the hot potato's blast ring.
import { Color } from 'three';
import { TUNE } from '@shared/constants';
import { BS, EK } from '@shared/ids';
import type { BeanView, EntityView, FrameView } from '../../game/FrameView';
import { Cell } from './atlas';
import type { DecalSystem } from './Decals';
import { C } from './effects';

export const TELEGRAPH = {
  teeterHz: 4,
  /** Reduced Flashing: softer, slower pulses (§14.4). */
  calmHz: 1.5,
  crateFallTicks: TUNE.crate.dropTicks,
  bombFlightTicks: TUNE.bomb.flightTicks,
  bombFuseTicks: TUNE.bomb.fuseTicks,
  potatoRingFrom: 9,
} as const;

/** Ground height at sim (x, y), NaN over the void. */
export type GroundFn = (x: number, y: number) => number;

const LILAC = new Color('#C7B8FF');
/** Sweat blue of the ledge-cooldown telegraph (the rig's sweat drops are #8FD8FF). */
const SWEAT = new Color('#5FC4FF');
/** The sweaty-hands ring fades out over the cooldown's last ticks. */
const GRIP_FADE_TICKS = 40;

interface EntMemo { firstZ: number; landedAt: number; seen: number }

export class Telegraphs {
  private readonly memo = new Map<number, EntMemo>();
  private frame = 0;
  private time = 0;
  /** Decals drawn last frame, by kind (verification). */
  readonly drawn = { teeter: 0, ledge: 0, punch: 0, crate: 0, bombFlight: 0, bombFuse: 0, pebble: 0, potato: 0, grip: 0 };

  constructor(private readonly decals: DecalSystem, private readonly ground: GroundFn) {}

  clear(): void {
    this.memo.clear();
  }

  update(view: FrameView, dt: number, calm: boolean): void {
    this.time += dt;
    this.frame++;
    const d = this.drawn;
    d.teeter = d.ledge = d.punch = d.crate = d.bombFlight = d.bombFuse = d.pebble = d.potato = d.grip = 0;
    for (const b of view.beans) {
      if (!b.active || b.ghost || b.out) continue;
      if (b.ledgeCd > 0 && b.state !== BS.LEDGE && b.state !== BS.CLIMB && b.state !== BS.FALLING) this.sweaty(b, b.ledgeCd, calm);
      if (b.state === BS.TEETER) this.teeter(b, calm);
      else if (b.state === BS.LEDGE) this.ledge(b, calm);
      else if (b.state === BS.PUNCH_WINDUP) this.punch(b);
      if (b.hasPotato && view.mode.potatoIntensity >= TELEGRAPH.potatoRingFrom) this.potato(b, view.mode.potatoIntensity, calm);
    }
    for (const e of view.ents) {
      let m = this.memo.get(e.id);
      if (!m) {
        m = { firstZ: Math.max(0.01, e.z), landedAt: -1, seen: 0 };
        this.memo.set(e.id, m);
      }
      m.seen = this.frame;
      switch (e.kind) {
        case EK.CRATE: this.crate(e, m); break;
        case EK.BOMB: this.bomb(e, m, view, calm); break;
        case EK.PEBBLE: this.pebble(e, m); break;
        default: break;
      }
    }
    if (this.frame % 60 === 0) for (const [id, m] of this.memo) if (m.seen !== this.frame) this.memo.delete(id);
  }

  private pulse(hz: number, calm: boolean, depth = 0.5): number {
    const f = calm ? Math.min(hz, TELEGRAPH.calmHz) : hz;
    const k = calm ? depth * 0.4 : depth;
    return 1 - k + k * (0.5 + 0.5 * Math.sin(this.time * f * Math.PI * 2));
  }

  private floor(x: number, y: number): number {
    const g = this.ground(x, y);
    return Number.isFinite(g) ? g : 0;
  }

  /** §11.2 teeter: a red pulsing glow under the bean, an ink-rimmed ring and an outward warning arc. */
  private teeter(b: BeanView, calm: boolean): void {
    const s = this.decals.spec;
    const h = this.floor(b.x, b.y);
    const p = this.pulse(TELEGRAPH.teeterHz, calm);
    s.reset();
    s.x = b.x;
    s.y = b.y;
    s.h = h;
    s.width = s.length = 1.9 + 0.25 * p;
    s.cell = Cell.GLOW;
    s.color(C.danger);
    s.alpha = 0.75 * p;
    this.decals.draw(s);
    s.cell = Cell.RING;
    s.width = s.length = 1.15 + 0.1 * p;
    s.alpha = 0.95;
    this.decals.draw(s);
    // A warning arc pointing out over the drop.
    s.reset();
    s.cell = Cell.ARC;
    s.angle = Math.atan2(-b.edgeNy, -b.edgeNx);
    const L = 1.5;
    s.x = b.x - b.edgeNx * L * 0.46;
    s.y = b.y - b.edgeNy * L * 0.46;
    s.h = h;
    s.width = s.length = L;
    s.color(C.danger);
    s.alpha = 0.55 * p;
    this.decals.draw(s);
    this.drawn.teeter++;
  }

  /** §11.2 ledge hang: a subtle red stomp-target ring around the hands on the rim. */
  private ledge(b: BeanView, calm: boolean): void {
    const s = this.decals.spec.reset();
    const p = this.pulse(2, calm, 0.35);
    s.x = b.edgeX;
    s.y = b.edgeY;
    s.h = this.floor(b.edgeX + b.edgeNx * 0.2, b.edgeY + b.edgeNy * 0.2);
    s.width = s.length = TUNE.edge.stompRadius * 2.2 * (0.95 + 0.08 * p);
    s.cell = Cell.TARGET;
    s.color(C.danger);
    s.alpha = 0.8 * p;
    this.decals.draw(s);
    this.drawn.ledge++;
  }

  /**
   * Ledge cooldown ("sweaty hands"): a glossy sweat puddle under the bean that breathes with a soft
   * sweat-blue glow, so a bean that just climbed back reads as "slippery: no grip left, the next edge
   * is a KO" (the VFX director adds a comic sweat drop on the temple and drips from the hands).
   */
  private sweaty(b: BeanView, ticksLeft: number, calm: boolean): void {
    const fade = Math.min(1, ticksLeft / GRIP_FADE_TICKS);
    const p = this.pulse(1.8, calm, 0.4);
    const s = this.decals.spec.reset();
    s.x = b.x;
    s.y = b.y;
    s.h = this.floor(b.x, b.y);
    s.width = s.length = (b.radius + 0.55) * 2 * (1 + 0.05 * p);
    s.cell = Cell.SOFTRING;
    s.color(SWEAT);
    s.alpha = 0.45 * p * fade;
    this.decals.draw(s);
    s.cell = Cell.PUDDLE;
    s.width = s.length = (b.radius + 0.38) * 2 * (0.96 + 0.06 * p);
    s.angle = b.slot * 1.7;
    s.alpha = 0.78 * fade;
    this.decals.draw(s);
    this.drawn.grip++;
  }

  /** §11.3 glove: a red arc telegraph on the ground over the punch reach while the spring coils. */
  private punch(b: BeanView): void {
    const reach = b.radius + TUNE.glove.reach + TUNE.bean.radius;
    const L = reach / 0.9;
    const k = Math.min(1, b.st / Math.max(1, TUNE.glove.windupTicks));
    const s = this.decals.spec.reset();
    s.cell = Cell.ARC;
    s.angle = b.facing;
    s.x = b.x + Math.cos(b.facing) * L * 0.46;
    s.y = b.y + Math.sin(b.facing) * L * 0.46;
    s.h = this.floor(b.x, b.y);
    s.width = s.length = L * (0.85 + 0.15 * k);
    s.color(C.danger);
    s.alpha = 0.45 + 0.45 * k;
    this.decals.draw(s);
    this.drawn.punch++;
  }

  /** Hot potato: the 3.5 m blast ring pulses around the carrier as the fuse runs hot. */
  private potato(b: BeanView, intensity: number, calm: boolean): void {
    const k = (intensity - TELEGRAPH.potatoRingFrom) / Math.max(1, TUNE.potato.intensityMax - TELEGRAPH.potatoRingFrom);
    const p = this.pulse(2 + 6 * k, calm, 0.6);
    const s = this.decals.spec.reset();
    s.x = b.x;
    s.y = b.y;
    s.h = this.floor(b.x, b.y);
    s.width = s.length = TUNE.potato.blastRadius * 2;
    s.cell = Cell.SOFTRING;
    s.color(C.danger);
    s.alpha = (0.25 + 0.35 * k) * p;
    this.decals.draw(s);
    this.drawn.potato++;
  }

  /** §11.3 crate drop: the shadow grows over the 1.0 s fall. */
  private crate(e: EntityView, m: EntMemo): void {
    if (e.z <= 0.05) return;
    const f = 1 - Math.min(1, e.z / m.firstZ);
    const s = this.decals.spec.reset();
    s.x = e.x;
    s.y = e.y;
    s.h = this.floor(e.x, e.y);
    s.width = s.length = TUNE.crate.radius * 2 * (0.45 + 0.85 * f);
    s.cell = Cell.DISC;
    s.color(C.ink);
    s.alpha = 0.18 + 0.4 * f;
    this.decals.draw(s);
    s.cell = Cell.TARGET;
    s.width = s.length = TUNE.crate.radius * 2 * (1.9 - 0.7 * f);
    s.color(C.yellow);
    s.alpha = 0.7;
    this.decals.draw(s);
    this.drawn.crate++;
  }

  private bomb(e: EntityView, m: EntMemo, view: FrameView, calm: boolean): void {
    const landed = e.data2 === 1 || e.z <= 0.05;
    if (!landed) {
      // Flight: the landing reticle where the arc comes down (36-tick flight, §3.3).
      const left = Math.max(0, TELEGRAPH.bombFlightTicks - e.t) / 60;
      const lx = e.x + e.vx * left;
      const ly = e.y + e.vy * left;
      const g = this.ground(lx, ly);
      if (!Number.isFinite(g)) return;
      const f = 1 - Math.min(1, left / (TELEGRAPH.bombFlightTicks / 60));
      const s = this.decals.spec.reset();
      s.x = lx;
      s.y = ly;
      s.h = g;
      s.width = s.length = 1.6 - 0.6 * f;
      s.cell = Cell.TARGET;
      s.color(C.danger);
      s.alpha = 0.85;
      this.decals.draw(s);
      s.cell = Cell.DISC;
      s.width = s.length = 0.5 + 0.4 * f;
      s.color(C.ink);
      s.alpha = 0.25 + 0.25 * f;
      this.decals.draw(s);
      this.drawn.bombFlight++;
      return;
    }
    if (m.landedAt < 0) m.landedAt = view.roundTick;
    const elapsed = view.roundTick - m.landedAt;
    const left = e.timer > 0 ? e.timer : Math.max(0, TELEGRAPH.bombFuseTicks - elapsed);
    const k = 1 - Math.min(1, left / TELEGRAPH.bombFuseTicks);
    const p = this.pulse(1.5 + 8.5 * k * k, calm, 0.7);
    const s = this.decals.spec.reset();
    s.x = e.x;
    s.y = e.y;
    s.h = this.floor(e.x, e.y);
    s.width = s.length = TUNE.bomb.radius * 2;
    s.cell = Cell.SOFTRING;
    s.color(C.danger);
    s.alpha = (0.3 + 0.4 * k) * p;
    this.decals.draw(s);
    s.cell = Cell.RING;
    s.width = s.length = TUNE.bomb.radius * 2 * 1.02;
    s.alpha = 0.25 + 0.55 * k * p;
    this.decals.draw(s);
    this.drawn.bombFuse++;
  }

  /** §2.12 pebble: a 0.6 s shadow telegraph that closes onto the impact point. */
  private pebble(e: EntityView, m: EntMemo): void {
    if (e.z <= 0.05) return;
    const f = 1 - Math.min(1, e.z / m.firstZ);
    const s = this.decals.spec.reset();
    s.x = e.x;
    s.y = e.y;
    s.h = this.floor(e.x, e.y);
    s.width = s.length = TUNE.ghost.pebbleRadius * 2 * (1.5 - 0.5 * f);
    s.cell = Cell.TARGET;
    s.color(LILAC);
    s.alpha = 0.85;
    this.decals.draw(s);
    s.cell = Cell.DISC;
    s.width = s.length = 0.3 + 0.35 * f;
    s.color(C.ink);
    s.alpha = 0.2 + 0.35 * f;
    this.decals.draw(s);
    this.drawn.pebble++;
  }
}
